import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowRight, Package, BarChart3, Users, Zap } from 'lucide-react';
import { toast } from 'sonner';

export default function Landing() {
    const navigate = useNavigate();
    const { isAuthenticated, isSuperAdmin } = useAuth();
    const [superAdminPassword, setSuperAdminPassword] = useState('');
    const [showSuperAdminModal, setShowSuperAdminModal] = useState(false);
    const { superAdminLogin } = useAuth();

    React.useEffect(() => {
        if (isAuthenticated) {
            if (isSuperAdmin) {
                navigate('/superadmin');
            } else {
                navigate('/dashboard');
            }
        }
    }, [isAuthenticated, isSuperAdmin, navigate]);

    const handleSuperAdminLogin = async (e) => {
        e.preventDefault();
        try {
            await superAdminLogin(superAdminPassword);
            toast.success('Welcome, Super Admin');
            navigate('/superadmin');
        } catch (error) {
            toast.error('Invalid password');
        }
    };

    const features = [
        {
            icon: Package,
            title: 'Product Management',
            description: 'Track your inventory with ease. Add products, set thresholds, and never run out of stock.'
        },
        {
            icon: ArrowRight,
            title: 'Stock IN/OUT',
            description: 'Record every movement. Simple, fast, and accurate stock tracking for your factory.'
        },
        {
            icon: BarChart3,
            title: 'Real-time Overview',
            description: 'See your stock levels instantly. Identify low stock items before they become a problem.'
        },
        {
            icon: Users,
            title: 'Team Access',
            description: 'Invite your team with role-based permissions. Everyone sees what they need.'
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="relative min-h-[90vh] flex items-center justify-center px-6 overflow-hidden">
                <div className="relative z-10 max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 mb-8">
                        <img src="/flowbil_logo.png" alt="Flowbil ERP" className="h-12" />
                    </div>
                    
                    <h1 className="text-5xl sm:text-6xl font-semibold text-gray-900 tracking-tight mb-6">
                        Track your inventory
                        <br />
                        <span className="text-gray-400">with clarity</span>
                    </h1>
                    
                    <p className="text-lg text-gray-500 max-w-2xl mx-auto mb-10">
                        Flowbil is a beautifully simple stock management system. 
                        Track products, record movements, and keep your factory running smoothly.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link
                            to="/signup"
                            className="btn btn-primary inline-flex items-center gap-2 text-base px-8 py-3"
                            data-testid="get-started-btn"
                        >
                            Get Started
                            <ArrowRight size={20} />
                        </Link>
                        <Link
                            to="/signin"
                            className="btn btn-ghost text-base"
                            data-testid="signin-link"
                        >
                            Sign In
                        </Link>
                    </div>

                    <div className="mt-8 flex items-center justify-center gap-6 text-sm text-gray-400">
                        <Link 
                            to="/employee-login" 
                            className="hover:text-gray-900 transition-colors"
                            data-testid="employee-login-link"
                        >
                            Employee Login
                        </Link>
                        <span>•</span>
                        <button
                            onClick={() => setShowSuperAdminModal(true)}
                            className="hover:text-gray-900 transition-colors"
                            data-testid="superadmin-link"
                        >
                            Super Admin
                        </button>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-24 bg-gray-50 px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-semibold text-gray-900 mb-4">Everything you need</h2>
                        <p className="text-gray-500 max-w-xl mx-auto">
                            Built for factory managers who need simplicity without sacrificing power.
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className="card p-8"
                            >
                                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-4">
                                    <feature.icon size={24} className="text-gray-600" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">{feature.title}</h3>
                                <p className="text-gray-500">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 px-6 bg-white">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-3xl font-semibold text-gray-900 mb-4">Ready to simplify your inventory?</h2>
                    <p className="text-gray-500 mb-8">
                        Create your company account and start tracking in minutes.
                    </p>
                    <Link
                        to="/signup"
                        className="btn btn-primary inline-flex items-center gap-2 text-base px-8 py-3"
                        data-testid="cta-signup-btn"
                    >
                        Create Your Account
                        <ArrowRight size={20} />
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-6 border-t border-gray-100">
                <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-sm text-gray-400">© 2024 Flowbil. Built for simplicity.</p>
                    <div className="flex items-center gap-6 text-sm text-gray-400">
                        <a href="#" className="hover:text-gray-900 transition-colors">Privacy</a>
                        <a href="#" className="hover:text-gray-900 transition-colors">Terms</a>
                    </div>
                </div>
            </footer>

            {/* Super Admin Modal */}
            {showSuperAdminModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
                        onClick={() => setShowSuperAdminModal(false)}
                    />
                    <div className="relative bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
                        <h3 className="text-lg font-medium text-gray-900 mb-6">Super Admin Access</h3>
                        <form onSubmit={handleSuperAdminLogin}>
                            <input
                                type="password"
                                value={superAdminPassword}
                                onChange={(e) => setSuperAdminPassword(e.target.value)}
                                placeholder="Enter password"
                                className="form-input mb-4"
                                data-testid="superadmin-password-input"
                                autoFocus
                            />
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowSuperAdminModal(false)}
                                    className="btn btn-secondary flex-1"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary flex-1"
                                    data-testid="superadmin-login-btn"
                                >
                                    Access
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
